/**
 * Duplication d'un exercice V2 : ops `insert` pour `exercise-blocks-mutate`
 * (chemin d'écriture unique, atomique). Pure et testée.
 *
 * Les parents sont émis AVANT leurs enfants : l'edge fn résout un `parent_id`
 * qui vaut le `client_id` d'un insert antérieur du même batch.
 */

import type { BlockMutationOp, ExerciseBlockSection, ExerciseBlockType } from './types';

export interface SourceBlock {
  id: string;
  parent_id: string | null;
  block_type: ExerciseBlockType;
  section: ExerciseBlockSection;
  position: number;
  data: Record<string, unknown>;
}

export function buildDuplicateBlockOps(blocks: readonly SourceBlock[]): BlockMutationOp[] {
  const byId = new Map(blocks.map((b) => [b.id, b]));
  const depth = (b: SourceBlock): number => {
    let d = 0;
    let cur: SourceBlock | undefined = b;
    const seen = new Set<string>();
    while (cur?.parent_id && byId.has(cur.parent_id) && !seen.has(cur.id)) {
      seen.add(cur.id);
      cur = byId.get(cur.parent_id);
      d += 1;
    }
    return d;
  };
  const ordered = [...blocks].sort((a, b) => depth(a) - depth(b) || a.position - b.position);
  return ordered.map((b) => ({
    op: 'insert' as const,
    client_id: `dup_${b.id}`,
    block_type: b.block_type,
    section: b.section,
    // Parent absent de la copie (bloc orphelin) → rattaché à la racine.
    parent_id: b.parent_id && byId.has(b.parent_id) ? `dup_${b.parent_id}` : null,
    position: b.position,
    data: b.data,
  }));
}
